import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { logUnknownErrorResponse } from '@hungryroot/o11y';
import {
  StatusCode,
  getApiV2PricingServiceCustomersCustomerIdProductSubscriptionCustomerConfig,
  getPricingServiceCustomerProductSubscriptionCustomerConfigPath,
  type PricingServiceCustomerProductSubscriptionCustomerConfigParams,
} from '@hungryroot/rest';
import {
  useNextOrderV3,
  useIdentify,
  useOrderDateParam,
} from '@hungryroot/rest-hooks';
import { Heading, Supplements } from '@hungryroot/ui-kit';
import styles from './usePdpSubscriptionBanner.module.css';

const timeLimit = 15 * 60 * 1000;

function useGetCustomerProductSubscriptionConfig(
  params: PricingServiceCustomerProductSubscriptionCustomerConfigParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [
      getPricingServiceCustomerProductSubscriptionCustomerConfigPath(params),
    ],
    queryFn: async () => {
      const resp =
        await getApiV2PricingServiceCustomersCustomerIdProductSubscriptionCustomerConfig(
          params
        );

      if (resp.status !== StatusCode.SuccessOK) {
        const error = JSON.stringify(resp.data, null, 2);
        logUnknownErrorResponse(
          'Failed to get customer product subscription config',
          {
            key: 'usePdpSubscriptionBanner.useGetCustomerProductSubscriptionConfig',
            error,
            params,
          }
        );
        throw new Error(error);
      }

      return resp.data;
    },
    ...options,
    retry: false,
    enabled: options?.enabled,
    gcTime: timeLimit,
    staleTime: timeLimit,
  });
}

const SUPPLEMENTS_CONTAINER_ID = 10;

export function usePdpSubscriptionBanner(
  productId?: number
): ReactNode | undefined {
  const { customerData } = useIdentify();
  const { id: customerId } = customerData || {};

  const { order: nextEditableOrder } = useNextOrderV3();
  const orderDate = useOrderDateParam(nextEditableOrder);

  const { data } = useGetCustomerProductSubscriptionConfig(
    {
      origin: nextEditableOrder?.origin,
      orderDate,
    },
    {
      enabled:
        customerId !== undefined && customerId > 0 && orderDate !== undefined,
    }
  );

  if (customerId === undefined || productId === undefined) {
    return;
  }

  const subscription = data?.results[0];

  if (
    subscription === undefined ||
    subscription.container_id !== SUPPLEMENTS_CONTAINER_ID
  ) {
    return;
  }

  const subscriptionElements = subscription.elements;

  const isProductInSubscription = subscriptionElements.some(
    (element) => element.product.id === productId
  );

  if (!isProductInSubscription) {
    return;
  }

  return (
    <div
      className={styles['supplements-banner']}
      data-testid={`supplements-banner-pdp-${productId}`}
    >
      <Heading variant="H6" className={styles['supplements-heading']}>
        <Supplements className={styles.supplements} />
        This item ships with your monthly supplements
      </Heading>
    </div>
  );
}
